import { Color, DefaultLoadingManager, FileLoader, FrontSide, Loader, LoaderUtils, LoadingManager, Mapping, Material, MeshPhongMaterial, RepeatWrapping, RGBAFormat, RGBFormat, Side, Texture, TextureLoader, Vector2, Wrapping } from 'three';
import { MaterialCreatorOptions, MaterialInfo } from 'three/examples/jsm/loaders/MTLLoader';

type IndexedMaterialInfo = Record<string, any> & MaterialInfo;

type MaterialsInfo = Record<string, IndexedMaterialInfo>;

interface TextureParams {
  scale: Vector2;
  offset: Vector2;
  url?: string;
}

export class MTLLoader extends Loader<MTLLoader.MaterialCreator> {

  constructor(manager?: LoadingManager) {
    super(manager);
  }

  public load(url: string,
              onLoad: (data: MTLLoader.MaterialCreator) => void,
              onProgress?: (event: ProgressEvent) => void,
              onError?: (err: unknown) => void,) {

    const path = (this.path === undefined) ? LoaderUtils.extractUrlBase(url) : this.path;
    const loader = new FileLoader(this.manager);
    loader.setPath(path);
    loader.load(url, (data: string | ArrayBuffer) => {

        if (data instanceof ArrayBuffer) {
          data = new TextDecoder().decode(data);
        }

        onLoad(this.parse(data, path));

      }, onProgress,
      onError);

  }

  public parse(text: string, path: string): MTLLoader.MaterialCreator {
    const lines = text.split('\n');
    let info: IndexedMaterialInfo = {};
    const delimiter_pattern = /\s+/;
    const materialsInfo: MaterialsInfo = {};

    for (let i = 0; i < lines.length; i++) {
      let line = lines[i];
      line = line.trim();

      if (line.length === 0 || line.charAt(0) === '#') {
        // Blank line or comment ignore
        continue;
      }

      const pos = line.indexOf(' ');

      let key = pos >= 0 ? line.substring(0, pos) : line;
      key = key.toLowerCase();

      let value = pos >= 0 ? line.substring(pos + 1) : '';
      value = value.trim();

      if (key === 'newmtl') {
        // New material
        info = {};
        materialsInfo[value] = info;
      } else {
        if (key === 'ka' || key === 'kd' || key === 'ks' || key === 'ke') {
          const ss = value.split(delimiter_pattern, 3);
          info[key] = [parseFloat(ss[0]), parseFloat(ss[1]), parseFloat(ss[2])];
        } else {
          info[key] = value;
        }
      }
    }

    const materialCreator = new MTLLoader.MaterialCreator(this.resourcePath || path);
    materialCreator.setCrossOrigin(this.crossOrigin);
    materialCreator.setManager(this.manager);
    materialCreator.setMaterials(materialsInfo);
    return materialCreator;
  }

  static ZIP_BASE_URL = 'zip/';
}

export namespace MTLLoader {

  /**
   * Modified version of the three js MTLLoader.MaterialCreator
   * Interface/Methods have to match the original three js MTLLoader.MaterialCreator
   */
  export class MaterialCreator {
    public baseUrl: string;
    public options: MaterialCreatorOptions;
    public materialsInfo: MaterialsInfo = {};
    public materials: { [key: string]: Material } = {};
    private materialsArray: Material[];
    public nameLookup: { [key: string]: number } = {};
    public side: Side;
    public wrap: Wrapping;
    public crossOrigin: string = 'anonymous';
    public manager?: LoadingManager;

    constructor(baseUrl?: string, options?: MaterialCreatorOptions) {
      this.baseUrl = baseUrl || '';
      this.options = options || {};
      this.side = FrontSide;
      this.wrap = RepeatWrapping;
      this.materialsArray = [];
    }

    public setCrossOrigin(value: string): MaterialCreator {
      this.crossOrigin = value;
      return this;
    }

    public setManager(value: LoadingManager): void {
      this.manager = value;
    }

    public setMaterials(materialsInfo: MaterialsInfo): void {
      this.materialsInfo = this.convert(materialsInfo);
      this.materials = {};
      this.materialsArray = [];
      this.nameLookup = {};
    }

    public convert(materialsInfo: MaterialsInfo): MaterialsInfo {

      if (!this.options) return materialsInfo;

      const converted: MaterialsInfo = {};

      for (const mn in materialsInfo) {

        // Convert materials info into normalized form based on options

        var mat: IndexedMaterialInfo = materialsInfo[mn];

        var covmat: IndexedMaterialInfo = {};

        converted[mn] = covmat;

        for (const prop in mat) {

          let save = true;
          let value = mat[prop];
          const lprop = prop.toLowerCase();

          switch (lprop) {

            case 'kd':
            case 'ka':
            case 'ks':
              // Diffuse color (color under white light) using RGB values
              if (this.options && this.options.normalizeRGB) {
                value = [value[0] / 255, value[1] / 255, value[2] / 255];
              }
              if (this.options && this.options.ignoreZeroRGBs) {
                if (value[0] === 0 && value[1] === 0 && value[2] === 0) {
                  // ignore
                  save = false;
                }
              }
              break;

            default:
              break;
          }

          if (save) {
            covmat[lprop] = value;
          }
        }
      }

      return converted;
    }

    public preload() {
      for (const mn in this.materialsInfo) {
        this.create(mn);
      }
    }

    public getIndex(materialName: string) {
      return this.nameLookup[materialName];
    }

    public getAsArray() {
      let index = 0;
      for (const mn in this.materialsInfo) {
        this.materialsArray[index] = this.create(mn);
        this.nameLookup[mn] = index;
        index++;
      }
      return this.materialsArray;
    }

    public create(materialName: string): Material {
      if (this.materials[materialName] === undefined) {
        this.createMaterial_(materialName);
      }

      return this.materials[materialName];
    }

    public createMaterial_(materialName: string): Material {
      // Create material
      const scope = this;
      const mat = this.materialsInfo[materialName];
      const params: any = {
        name: materialName,
        side: this.side
      };

      function resolveURL(baseUrl: string, url: string): string {
        if (typeof url !== 'string' || url === '') return '';

        // Absolute URL
        if (/^https?:\/\//i.test(url)) return url;

        return baseUrl + url;
      }

      function setMapForType(mapType: string, value: string): void {
        if (params[mapType]) return; // Keep the first encountered texture

        const texParams = scope.getTextureParams(value, params);
        const map = scope.loadTexture(resolveURL(scope.baseUrl, texParams.url || ''));

        map.repeat.copy(texParams.scale);
        map.offset.copy(texParams.offset);

        map.wrapS = scope.wrap;
        map.wrapT = scope.wrap;

        params[mapType] = map;
      }

      for (const prop in mat) {
        const value = mat[prop];
        let n: number;

        if (value === '') continue;

        switch (prop.toLowerCase()) {
          // Ns is material specular exponent

          case 'kd':
            // Diffuse color (color under white light) using RGB values
            params.color = new Color().fromArray(value);
            break;

          case 'ks':
            // Specular color (color when light is reflected from shiny surface) using RGB values
            params.specular = new Color().fromArray(value);
            break;

          case 'ke':
            // Emissive using RGB values
            params.emissive = new Color().fromArray(value);
            break;

          case 'map_kd':
            // Diffuse texture map
            setMapForType('map', value);
            break;

          case 'map_ks':
            // Specular map
            setMapForType('specularMap', value);
            break;

          case 'map_ke':
            // Emissive map
            setMapForType('emissiveMap', value);
            break;

          case 'norm':
            setMapForType('normalMap', value);
            break;

          case 'map_bump':
          case 'bump':
            // Bump texture map
            setMapForType('bumpMap', value);
            break;

          case 'map_d':
            // Alpha map
            setMapForType('alphaMap', value);
            params.transparent = true;
            break;

          case 'ns':
            // The specular exponent (defines the focus of the specular highlight)
            // A high exponent results in a tight, concentrated highlight. Ns values normally range from 0 to 1000.
            params.shininess = parseFloat(value);
            break;

          case 'd':
            n = parseFloat(value);

            if (n < 1) {
              params.opacity = n;
              params.transparent = true;
            }
            break;

          case 'tr':
            n = parseFloat(value);

            if (n > 0) {
              params.opacity = 1 - n;
              params.transparent = true;
            }
            break;

          default:
            break;
        }
      }

      this.materials[materialName] = new MeshPhongMaterial(params);
      return this.materials[materialName];
    }

    public getTextureParams(value: string, matParams: any): TextureParams {
      const texParams: TextureParams = {
        scale: new Vector2(1, 1),
        offset: new Vector2(0, 0)
      };

      const items = value.split(/\s+/);
      let pos: number;

      pos = items.indexOf('-bm');

      if (pos >= 0) {
        matParams.bumpScale = parseFloat(items[pos + 1]);
        items.splice(pos, 2);
      }

      pos = items.indexOf('-s');

      if (pos >= 0) {
        texParams.scale.set(parseFloat(items[pos + 1]), parseFloat(items[pos + 2]));
        items.splice(pos, 4); // we expect 3 parameters here!
      }

      pos = items.indexOf('-o');

      if (pos >= 0) {
        texParams.offset.set(parseFloat(items[pos + 1]), parseFloat(items[pos + 2]));
        items.splice(pos, 4); // we expect 3 parameters here!
      }

      texParams.url = items.join(' ').trim();
      return texParams;
    }

    public loadTexture(
      url: string,
      mapping?: Mapping,
      onLoad?: (texture: unknown) => void,
      onProgress?: (event: ProgressEvent) => void,
      onError?: (event: unknown) => void
    ): Texture {
      let texture: Texture;

      if (this.baseUrl === MTLLoader.ZIP_BASE_URL) {
        texture = new Texture();
        const isJPEG = url.search(/\.jpe?g($|\?)/i) > 0 || url.search(/^data:image\/jpeg/) === 0;
        texture.format = isJPEG ? RGBFormat : RGBAFormat;
        texture.image = new Image();
        texture.image.src = url;
      } else {
        const manager = this.manager !== undefined ? this.manager : DefaultLoadingManager;
        const loader = new TextureLoader(manager);
        if (loader.setCrossOrigin) {
          loader.setCrossOrigin(this.crossOrigin);
        }
        return loader.load(url, onLoad, onProgress, onError);
      }

      if (mapping !== undefined) texture!.mapping = mapping;

      return texture!;
    }
  }
}
